import type { SidHeader } from '../../core/types';
import type { CaptureSummary } from '../../worker/protocol';
import { hex } from '../util';

interface Props {
  header: SidHeader;
  capture: CaptureSummary | null;
}

export function FileInfo({ header: h, capture }: Props) {
  const rows: [string, string][] = [
    ['Title', h.name || '—'],
    ['Author', h.author || '—'],
    ['Released', h.released || '—'],
    ['Format', `${h.format} v${h.version}`],
    ['SID model', h.sidModel === 'unknown' ? 'Unknown' : h.sidModel === 'both' ? '6581 / 8580' : h.sidModel],
    ['Clock', h.clock === 'unknown' ? 'Unknown (PAL assumed)' : h.clock === 'both' ? 'PAL / NTSC' : h.clock],
    [
      'Load / init / play',
      `${hex(h.loadAddress, 4)} / ${hex(h.initAddress, 4)} / ${h.playAddress ? hex(h.playAddress, 4) : 'IRQ'}`,
    ],
  ];
  if (h.sidCount > 1) rows.push(['SID chips', String(h.sidCount)]);
  if (capture) {
    rows.push(['Timing', `${capture.speedMode} · ${capture.tickRate.toFixed(2)} Hz`]);
    rows.push([
      'Length',
      `${(capture.ticks / capture.tickRate).toFixed(1)} s (${
        capture.endReason === 'loop'
          ? 'loop detected'
          : capture.endReason === 'silence'
            ? 'ended in silence'
            : 'duration limit'
      })`,
    ]);
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12px]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd className="min-w-0 truncate font-medium" title={v}>
            {v}
          </dd>
        </div>
      ))}
    </dl>
  );
}

interface SubtuneProps {
  header: SidHeader;
  value: number;
  onChange: (subtune: number) => void;
}

export function SubtuneSelector({ header, value, onChange }: SubtuneProps) {
  const current = value || header.startSong;
  return (
    <label className="flex flex-col gap-1">
      <span className="label">
        Subtune ({header.songs} in file, default {header.startSong})
      </span>
      <select
        className="field"
        value={current}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={header.songs < 2}
      >
        {Array.from({ length: header.songs }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n}
            {n === header.startSong ? ' (default)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}
